import React, { useRef, useMemo, useEffect } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { Stars } from "@react-three/drei";
import * as THREE from "three";
import { useDayFactor, makeColorLerper } from "../context";
import { COLORS, DAY_COLORS } from "../constants";
import DynamicSky from "./DynamicSky";

export default function DynamicLighting() {
  const dayRef = useDayFactor();
  const ambientRef = useRef();
  const hemiRef = useRef();
  const sunRef = useRef();
  const warmRef = useRef();
  const starsGroupRef = useRef();
  const { scene } = useThree();

  const lerp = useMemo(() => makeColorLerper(), []);
  const colors = useMemo(
    () => ({
      ambient: new THREE.Color(),
      hemiSky: new THREE.Color(),
      hemiGround: new THREE.Color(),
      sun: new THREE.Color(),
      warm: new THREE.Color(),
      fog: new THREE.Color(),
      bg: new THREE.Color(),
    }),
    []
  );

  // Set transparent/depthWrite once after Stars mount (avoids per-frame GC churn).
  useEffect(() => {
    const id = setTimeout(() => {
      if (starsGroupRef.current) {
        starsGroupRef.current.traverse((o) => {
          if (o.material) {
            o.material.transparent = true;
            o.material.depthWrite = false;
          }
        });
      }
    }, 50);
    return () => clearTimeout(id);
  }, []);

  useFrame(() => {
    const f = dayRef.current;
    const night = 1 - f;

    if (ambientRef.current) {
      ambientRef.current.intensity = 0.32 + f * 0.25;
      lerp(colors.ambient, COLORS.navy, DAY_COLORS.ambient, f);
      ambientRef.current.color.copy(colors.ambient);
    }
    if (hemiRef.current) {
      hemiRef.current.intensity = 0.45 + f * 0.55;
      lerp(colors.hemiSky, COLORS.navy, DAY_COLORS.hemiSky, f);
      lerp(colors.hemiGround, COLORS.grassDark, DAY_COLORS.hemiGround, f);
      hemiRef.current.color.copy(colors.hemiSky);
      hemiRef.current.groundColor.copy(colors.hemiGround);
    }
    if (sunRef.current) {
      sunRef.current.intensity = 0.55 + f * 1.6;
      lerp(colors.sun, "#bcd4ff", DAY_COLORS.sun, f);
      sunRef.current.color.copy(colors.sun);
    }
    if (warmRef.current) {
      warmRef.current.intensity = 0.2 + f * 0.6 + night * 0.15;
      lerp(colors.warm, COLORS.gold, DAY_COLORS.warmFill, f);
      warmRef.current.color.copy(colors.warm);
    }
    lerp(colors.fog, COLORS.navyMid, DAY_COLORS.fogNear, f);
    if (scene.fog) scene.fog.color.copy(colors.fog);
    lerp(colors.bg, COLORS.twilightTop, DAY_COLORS.skyMid, f);
    if (scene.background && scene.background.isColor) {
      scene.background.copy(colors.bg);
    }
    if (starsGroupRef.current) {
      starsGroupRef.current.traverse((o) => {
        if (o.material) {
          o.material.opacity = Math.max(0, 1 - f * 1.1);
        }
      });
    }
  });

  return (
    <>
      <color attach="background" args={[COLORS.twilightTop]} />
      <fog attach="fog" args={[COLORS.navyMid, 30, 120]} />

      <DynamicSky />

      <group ref={starsGroupRef}>
        <Stars radius={120} depth={60} count={2500} factor={3} saturation={0.2} fade speed={0.5} />
      </group>

      <ambientLight ref={ambientRef} intensity={0.32} color={COLORS.navy} />
      <hemisphereLight ref={hemiRef} args={[COLORS.navy, COLORS.grassDark, 0.45]} />
      <directionalLight
        ref={sunRef}
        position={[30, 40, 20]}
        intensity={0.55}
        color={"#bcd4ff"}
        castShadow
        shadow-mapSize-width={1024}
        shadow-mapSize-height={1024}
        shadow-camera-far={120}
        shadow-camera-left={-60}
        shadow-camera-right={60}
        shadow-camera-top={60}
        shadow-camera-bottom={-60}
      />
      <directionalLight
        ref={warmRef}
        position={[-30, 10, -10]}
        intensity={0.35}
        color={COLORS.gold}
      />
    </>
  );
}
